import type { FunnelMetrics, FunnelStageMetric } from "../types";

/**
 * Almikia Epic ER (TASK-004, vision #13): a pure, rule-based (no AI)
 * one-sentence bottleneck narrative built directly from
 * `get_funnel_metrics()`'s own figures (`FunnelMetrics`, `useFunnelMetrics.ts`)
 * -- never a client-side guess, never digits/percent/English jargon, never an
 * Independence-guide banned term (see the ticket's own list: empleado, jefe,
 * horario, turno, guardia, asistencia, puntualidad, sancion, apercibimiento,
 * falta, obligatorio, ranking), always second person plain Spanish. Asserted
 * by `funnelNarrative.test.ts`, not just documented here.
 *
 * "Stuck" is read from `stuck_open_count` (open leads already past their
 * stage's configured target time, `FunnelStageMetric`) -- the real-time
 * signal that best matches "se atoran" (jam up). When no stage currently has
 * a stuck lead, the stage with the biggest conversion drop-off
 * (`reached_count - advanced_count`) is named instead, since it still shows
 * where most leads fail to move forward. With too little data (no stage
 * reached by any lead) or no signal at all (every lead moves cleanly), a
 * friendly Spanish sentence is returned instead of naming a stage.
 */
export function buildFunnelBottleneckNarrative(
  metrics: Pick<FunnelMetrics, "stages">,
  stageLabelByKey: ReadonlyMap<string, string>,
): string {
  const stagesWithData = metrics.stages.filter(
    (stage) => stage.reached_count > 0,
  );
  if (stagesWithData.length === 0) {
    return "Todavía no tienes leads suficientes para saber dónde se atoran.";
  }

  const label = (stage: FunnelStageMetric) =>
    stageLabelByKey.get(stage.stage_key) ?? stage.stage_key;

  const stuckStage = [...stagesWithData]
    .filter((stage) => stage.stuck_open_count > 0)
    .sort((a, b) => b.stuck_open_count - a.stuck_open_count)[0];
  if (stuckStage) {
    return `La mayoria de tus leads se atoran en ${label(stuckStage)}.`;
  }

  const dropOffStage = [...stagesWithData]
    .map((stage) => ({
      stage,
      dropOff: stage.reached_count - stage.advanced_count,
    }))
    .sort((a, b) => b.dropOff - a.dropOff)[0];
  if (dropOffStage && dropOffStage.dropOff > 0) {
    return `La mayoria de tus leads se atoran en ${label(dropOffStage.stage)}.`;
  }

  return "Tus leads avanzan bien: no hay una etapa donde se atoren más que en otras.";
}
